import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  ButtonVariant,
  Dialog,
  PRODUCT_NAMES,
  isProductSelectable,
} from "@merge-api/merge-javascript-shared";
import type { ProductKey } from "@merge-api/merge-javascript-shared";
import useAppContext from "../../context/useAppContext";
import { showErrorToast } from "../../shared/Toasts";
import { forgetSignupIntent, readSignupIntent } from "../../shared/utils/signupIntent";
import useProductSwitcher from "../useProductSwitcher";
import ProductChoice from "./components/ProductChoice";
import SwitchProgress from "./components/SwitchProgress";
import {
  EXPLORE_MERGE_OPTIONS,
  EXPLORE_MERGE_TITLE,
  MFP_SIGNUP_INTENT,
  SAVE_FAILED_MESSAGE,
  SETTING_UP_ACCOUNT_MESSAGE,
  takingYouToMessage,
} from "./constants";
import useSetupWait from "./hooks/useSetupWait";
import saveDefaultProduct from "./utils/saveDefaultProduct";
import submitDefaultProductForm from "./utils/submitDefaultProductForm";

interface Props {
  onOpenChange: (isOpen: boolean) => void;
}

interface PendingSetup {
  product: ProductKey;
  seconds: number;
  formSubmitted: Promise<void>;
}

// The shared Dialog always renders a title row and divider, so the switch step hides both.
const HEADERLESS_DIALOG = "[&>div:first-child]:hidden [&>hr]:hidden";

const ExploreMergeModal = ({ onOpenChange }: Props) => {
  // state
  const [selectedProduct, setSelectedProduct] = useState<ProductKey | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSettingUpAccount, setIsSettingUpAccount] = useState(false);
  const [pendingSetup, setPendingSetup] = useState<PendingSetup | null>(null);
  const [cameFromMfpLink, setCameFromMfpLink] = useState(
    () => readSignupIntent(new URLSearchParams(window.location.search)) === MFP_SIGNUP_INTENT,
  );

  // hooks
  const { user, setUser } = useAppContext();
  const switcher = useProductSwitcher();
  useSetupWait({
    durationSeconds: pendingSetup?.seconds ?? null,
    onDone: () => {
      if (pendingSetup === null) return;
      const { product, formSubmitted } = pendingSetup;
      // Leaving the page cancels the HubSpot form request if it is still in flight.
      formSubmitted.then(() => {
        // Switch first: clearing the wait before the switch is pending would close the dialog for a frame.
        switcher.switchTo(product);
        setPendingSetup(null);
      });
    },
  });

  // derived state
  const isChoicePending =
    user.signup_intent === MFP_SIGNUP_INTENT && !user.default_product && switcher.enabled;
  const defaultProduct = user.default_product ?? null;
  const returnTarget =
    cameFromMfpLink &&
    switcher.enabled &&
    switcher.products.some(
      (entry) => entry.product === defaultProduct && entry.status === "available",
    )
      ? defaultProduct
      : null;
  const leavingTo = pendingSetup?.product ?? switcher.switchingTo ?? returnTarget;
  const isOpen = isChoicePending || isSaving || leavingTo !== null;
  const leavingMessage =
    leavingTo !== null && !isSettingUpAccount
      ? takingYouToMessage(PRODUCT_NAMES[leavingTo])
      : SETTING_UP_ACCOUNT_MESSAGE;
  const options = EXPLORE_MERGE_OPTIONS.filter(({ product }) =>
    switcher.products.some(
      (entry) => entry.product === product && isProductSelectable(entry.status),
    ),
  );

  // effects
  // Sends a user who came back through the tile to the product they picked, once, so later loads in the tab stay put.
  useEffect(() => {
    if (!cameFromMfpLink) return;
    if (returnTarget !== null) switcher.switchTo(returnTarget);
    forgetSignupIntent();
    setCameFromMfpLink(false);
  }, [cameFromMfpLink, returnTarget, switcher]);

  // AppRouter holds Frigade's popups back while this is open; before paint, so none shows over it.
  useLayoutEffect(() => onOpenChange(isOpen), [isOpen, onOpenChange]);

  // event handlers
  const onGetStarted = () => {
    if (selectedProduct === null || isSaving || leavingTo !== null) return;
    setIsSaving(true);
    saveDefaultProduct(selectedProduct)
      .then((savedUser) => {
        setUser(savedUser);
        const formSubmitted = submitDefaultProductForm(savedUser.email, selectedProduct);
        if (selectedProduct === switcher.currentProduct) return;
        setIsSettingUpAccount(true);
        const seconds = savedUser.product_switcher?.ready_in_seconds ?? 0;
        setPendingSetup({ product: selectedProduct, seconds, formSubmitted });
      })
      .catch(() => showErrorToast(SAVE_FAILED_MESSAGE))
      .finally(() => setIsSaving(false));
  };

  // render
  return (
    <Dialog
      open={isOpen}
      onClose={() => undefined}
      title={leavingTo === null ? EXPLORE_MERGE_TITLE : ""}
      variant={leavingTo === null ? "lg" : "md"}
      className={leavingTo === null ? undefined : HEADERLESS_DIALOG}
      bodyClassName={leavingTo === null ? undefined : "px-6 py-16"}
      xButtonHidden
      disableCloseOnBackdropClick
      hideSecondaryButton
      compactFooterButtons
      footerButtonsHidden={leavingTo !== null}
      primaryButtonText="Get started"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonDisabled={selectedProduct === null}
      primaryButtonLoading={isSaving}
      onPrimaryButtonClick={onGetStarted}
    >
      {leavingTo === null ? (
        <ProductChoice
          options={options}
          selectedProduct={selectedProduct}
          onSelectProduct={setSelectedProduct}
        />
      ) : (
        <SwitchProgress message={leavingMessage} />
      )}
    </Dialog>
  );
};

export default ExploreMergeModal;
